/**
 * VendorReportedAmountsSection - Displays vendor-reported transaction data
 *
 * Shows:
 * - Rebate invoices table with file information
 * - Transaction volume by division (monthly breakdown)
 * - Transaction volume by product group (monthly breakdown)
 * - Toggle to show/hide non-rebateable items
 * - Cumulative vs month-by-month toggle (controlled from parent via FAB)
 */

import React, { useState } from 'react';
import {
  ReceiptIcon,
  CircleNotchIcon,
  CaretDownIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

import { formatPaymentDate } from 'src/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import type { RebateInvoice } from '@/api/rebates/types';
import type { RebateLineItem } from '@/api/rebates/useRebateLineItems';
import { Element } from 'react-scroll';
import { Link } from '@tanstack/react-router';

import { VendorReportedAmountsTables } from '@/components/rebates/VendorReportedAmountsTables';

interface VendorReportedAmountsSectionProps {
  // Refs and state
  invoicesRef: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: (open: boolean) => void;

  // Data
  invoices?: RebateInvoice[];
  invoicesLoading: boolean;
  lineItems?: RebateLineItem[];
  lineItemsLoading: boolean;
  lastDataIngestionDate: string | null;

  // Cumulative toggle (controlled from parent)
  isCumulative: boolean;

  // Date range context
  selectedYear: number | null;
  agreementStartDate?: string;

  // Filters (from FAB)
  selectedDivisionIds?: Set<string>;

  // Handlers
  onViewDocument: (documentId: string, fileName?: string) => void;
  onDeleteInvoice: (invoiceId: string) => void;

  // Optional: migration wizard trigger
  showMigrationWizard?: boolean;
  onOpenMigrationWizard?: () => void;

  // Supplier ID for navigation to supplier invoices page
  supplierId?: string;
}

export function VendorReportedAmountsSection({
  invoicesRef,
  isExpanded,
  onToggle,
  invoices,
  invoicesLoading,
  lineItems,
  lineItemsLoading,
  lastDataIngestionDate,
  isCumulative,
  selectedDivisionIds,
  onViewDocument,
  onDeleteInvoice,
  showMigrationWizard,
  onOpenMigrationWizard,
  supplierId,
}: VendorReportedAmountsSectionProps) {
  // Toggle for showing/hiding non-rebateable items (default: show)
  const [showNonRebateable, setShowNonRebateable] = useState(true);

  return (
    <Element name="vendor_reported_amounts">
      <div ref={invoicesRef}>
        <Card>
          <Collapsible open={isExpanded} onOpenChange={onToggle}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-between w-full mr-4">
                    <div className="flex items-center gap-3">
                      <ReceiptIcon className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold">
                        Vendor Reported Amounts ({invoices?.length || 0})
                      </h2>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* View All Supplier Reported Amounts Button */}
                      {supplierId && (
                        <Link
                          to="/rebates/supplier-reported-amounts/$supplierId"
                          params={{ supplierId }}
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm" className="gap-1">
                            <ArrowSquareOutIcon className="h-3.5 w-3.5" />
                            View All Reported Amounts
                          </Button>
                        </Link>
                      )}
                      {/* Migrate CSVs Button */}
                      {showMigrationWizard && onOpenMigrationWizard && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenMigrationWizard();
                          }}
                        >
                          Migrate CSVs
                        </Button>
                      )}
                      {lastDataIngestionDate && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                              >
                                Last Data:{' '}
                                {formatPaymentDate(lastDataIngestionDate)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Last date of real data ingestion (max date of
                                all invoice line item ranges)
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {invoices && invoices.length > 0 && (
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold text-green-600">
                            Rebateable:{' '}
                            {formatCurrency(
                              invoices.reduce(
                                (sum, invoice) =>
                                  sum + invoice.totalRebateableAmount,
                                0,
                              ),
                            )}
                          </span>
                          <span className="text-lg font-bold">
                            Total:{' '}
                            {formatCurrency(
                              invoices.reduce(
                                (sum, invoice) => sum + invoice.totalAmount,
                                0,
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180 flex-shrink-0" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <VendorReportedAmountsTables
                  invoices={invoices}
                  invoicesLoading={invoicesLoading}
                  lineItems={lineItems}
                  lineItemsLoading={lineItemsLoading}
                  isCumulative={isCumulative}
                  selectedDivisionIds={selectedDivisionIds}
                  showNonRebateable={showNonRebateable}
                  onShowNonRebateableChange={setShowNonRebateable}
                  onViewDocument={onViewDocument}
                  onDeleteInvoice={onDeleteInvoice}
                />
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>
    </Element>
  );
}
